import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListChips from "_shared/_views/list-pages/list-chips";
import type { ListChipCounts, ListChipSelection } from "_shared/_views/list-pages/list-chip-selection";
import {
  ListPageHeading,
  ListPageMobileSearch,
  ListPageMobileToolbar,
  ListPageTabs,
  ListPageToolbarButtons,
  type ListPageTab,
} from "_shared/_views/list-pages/list-page-heading";
import {
  searchFieldSpacing,
  TableActionButton,
  TableActionIconButton,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { teamsChipGroups } from "_shared/teams/teams-list-chips";
import { PageTab } from "_shared/teams/teams-tabs";
import {
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_RowData as RowData,
  type MRT_TableInstance as TableInstance,
} from "material-react-table";
import useSessionUser from "permissions/session/use-session-user";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { RootState } from "_shared/_stores/redux-store";

/** Where Create Template goes, from either tab. */
const CREATE_TEMPLATE_PATH = `/teams/${PageTab.Templates}/create`;

/**
 * Creating a team template needs read/write access, a higher bar than the read access that gets
 * someone onto this page. Saying so on the button beats letting them build a whole template and meet
 * the API's 403 at Save (issue #4948).
 */
function useCreateTemplate() {
  const sessionUser = useSessionUser();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const canCreate = sessionUser?.canCreateTeamTemplates() ?? false;
  let tooltip = "Creating a team template requires read/write team access. Ask an administrator at your firm.";
  if (!sessionPermissions && !sessionPermissionsError) tooltip = "Loading team permissions...";
  else if (canCreate) tooltip = "Create Template";
  return { canCreate, tooltip };
}

type TeamsToolbarProps<TData extends RowData> = {
  table: TableInstance<TData>;
  activeTab: PageTab;
  tabs: readonly ListPageTab<PageTab>[];
  onTabChange: (tab: PageTab) => void;
  /** The tab's chips: Teams' perspectives, or Templates' Archived chip (#6718). */
  chips: ListChipSelection;
  chipCounts?: ListChipCounts;
  onChipsChange: (selection: ListChipSelection) => void;
};

/**
 * The Teams page's heading (#6718): the title, the Teams and Templates tabs, the tab's chips, and on a
 * narrow screen the create action, the search and the table tools. Both tabs draw it, so a reader finds
 * each control in the same place on either.
 */
export default function TeamsToolbar<TData extends RowData>(props: Readonly<TeamsToolbarProps<TData>>) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized element
  // drawn with it never re-renders: the column list kept the switches it first drew (#6599).
  "use no memo";

  const { table, activeTab, tabs, onTabChange, chips, chipCounts, onChipsChange } = props;
  const navigate = useNavigate();
  const createTemplate = useCreateTemplate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  return (
    <ListPageHeading
      title="Teams"
      titleAction={
        isMediumDisplaySize ? (
          <Tooltip title={createTemplate.tooltip}>
            <span>
              <TableActionIconButton
                aria-label="Create Template"
                color="primary"
                disabled={!createTemplate.canCreate}
                onClick={() => navigate(CREATE_TEMPLATE_PATH)}
              >
                <GroupAddIcon fontSize="large" />
              </TableActionIconButton>
            </span>
          </Tooltip>
        ) : undefined
      }
      tabs={<ListPageTabs value={activeTab} tabs={tabs} onChange={(_event, tab) => onTabChange(tab)} />}
      chips={
        <ListChips groups={teamsChipGroups[activeTab]} selection={chips} counts={chipCounts} onChange={onChipsChange} />
      }
      mobileToolbar={
        isMediumDisplaySize ? (
          <ListPageMobileToolbar>
            <ListPageMobileSearch table={table} />
            <TableToolsMenu table={table} />
          </ListPageMobileToolbar>
        ) : undefined
      }
    />
  );
}

/** The wide layout's search, table tools and Create Template, at the right of the heading. */
export function TeamsInternalActions<TData extends RowData>(props: Readonly<{ table: TableInstance<TData> }>) {
  // MRT's table instance is one mutable object, the same prop on every render (#6599).
  "use no memo";

  const { table } = props;
  const navigate = useNavigate();
  const createTemplate = useCreateTemplate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  // Narrow screens get this same set from the heading, where it can sit under the tabs instead of
  // beside them. Without the guard both rows draw and the page shows two of every control.
  if (isMediumDisplaySize) return null;

  return (
    <ListPageToolbarButtons>
      <ToolbarButtons>
        <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
        <TableToolsMenu table={table} />
      </ToolbarButtons>
      <Tooltip title={createTemplate.tooltip}>
        <span>
          <TableActionButton
            startIcon={<GroupAddIcon />}
            disabled={!createTemplate.canCreate}
            onClick={() => navigate(CREATE_TEMPLATE_PATH)}
          >
            Create Template
          </TableActionButton>
        </span>
      </Tooltip>
    </ListPageToolbarButtons>
  );
}
